import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { Box } from "theme-ui";
import { noop } from "sites-common/utils/lodash";
import OnlineSection from "../OnlineSection";
import NetBankingSection from "../NetBankingSection";
import WalletSection from "../WalletSection";
import ECSSection from "../ECSSection";
import { FlexColumn, RadiosWithLabel } from "../../../index";

export const useMonthlyRecurringDonation = (args = {}) => {
  const {
    isMonthlyRecurringDonation = false,
    donation_type = "ONLINE",
    onlineSectionDetails = {
      account_type: "Savings",
      end_date: null,
      start_date: null,
      untilCancelled: true,
    },
    offlineSectionDetails = {
      payment_option:
        "Standing Instruction with donor’s bank for making recurring donations",
      standingInstructionDetails: {
        payment_mode: "Internet banking",
        netBankingDetails: {
          donor_bank: "YES BANK",
          standing_date: null,
          donor_ifsc_code: "",
        },
        walletDetails: {
          wallet_mobile: "",
          standing_date: null,
        },
      },
    },
    onChange = noop,
    isOfflinePage = false,
  } = args;

  const onChangeMonthlyRecurringSection = React.useCallback(
    (update) => {
      onChange({
        isMonthlyRecurringDonation,
        donation_type,
        onlineSectionDetails,
        offlineSectionDetails,
        ...update,
      });
    },
    [
      isMonthlyRecurringDonation,
      donation_type,
      onlineSectionDetails,
      offlineSectionDetails,
      onChange,
    ]
  );

  const onChangeOfflineSectionDetails = React.useCallback(
    (update) => {
      onChangeMonthlyRecurringSection({
        offlineSectionDetails: {
          ...offlineSectionDetails,
          ...update,
        },
      });
    },
    [onChangeMonthlyRecurringSection, offlineSectionDetails]
  );

  const onChangeStandingInstructionDetails = React.useCallback(
    (update) => {
      onChangeOfflineSectionDetails({
        standingInstructionDetails: {
          ...offlineSectionDetails.standingInstructionDetails,
          ...update,
        },
      });
    },
    [
      onChangeOfflineSectionDetails,
      offlineSectionDetails.standingInstructionDetails,
    ]
  );

  /* facing lint issue and not able to resolve with dependency array */

  // const onChangeNetBankingDetails = React.useCallback(
  //   (update) => {
  //     onChangeStandingInstructionDetails({
  //       netBankingDetails: {
  //         ...offlineSectionDetails.standingInstructionDetails.netBankingDetails,
  //         ...update,
  //       },
  //     });
  //   },
  //   [
  //     onChangeStandingInstructionDetails,
  //     offlineSectionDetails.standingInstructionDetails.netBankingDetails,
  //   ]
  // );

  const monthlyRecurringSwitch = React.useMemo(
    () => ({
      defaultChecked: isMonthlyRecurringDonation,
      onChange: ({ target: { checked: isMonthlyRecurringDonationCheck } }) =>
        onChangeMonthlyRecurringSection({
          isMonthlyRecurringDonation: isMonthlyRecurringDonationCheck,
        }),
    }),
    [isMonthlyRecurringDonation, onChangeMonthlyRecurringSection]
  );

  const donationTypeOptions = React.useMemo(
    () => [
      {
        value: "ONLINE",
        label: "Online",
      },
      {
        value: "OFFLINE",
        label: "Offline",
      },
    ],
    []
  );

  const paymentOptions = React.useMemo(
    () => [
      {
        value:
          "Standing Instruction with donor’s bank for making recurring donations",
        label: "Standing Instruction for banking",
      },
      { value: "ECS Donations", label: "ECS Donations" },
    ],
    []
  );

  const paymentOptionRadios = React.useMemo(
    () => ({
      name: "payment_option",
      options: paymentOptions,
      value: offlineSectionDetails.payment_option,
      onChange: (payment_option) =>
        onChangeOfflineSectionDetails({
          payment_option,
        }),
    }),
    [onChangeOfflineSectionDetails, paymentOptions, offlineSectionDetails]
  );

  const paymentModes = React.useMemo(
    () => [
      { value: "Internet banking", label: "Net Banking" },
      { value: "WALLET", label: "Wallet" },
    ],
    []
  );

  const paymentModeRadios = React.useMemo(
    () => ({
      name: "payment_mode",
      options: paymentModes,
      value: offlineSectionDetails.standingInstructionDetails.payment_mode,
      onChange: (payment_mode) =>
        onChangeStandingInstructionDetails({
          payment_mode,
        }),
    }),
    [offlineSectionDetails, paymentModes, onChangeStandingInstructionDetails]
  );

  useEffect(() => {
    if (isOfflinePage && donation_type !== "OFFLINE") {
      onChangeMonthlyRecurringSection({ donation_type: "OFFLINE" });
    }
  }, [isOfflinePage, onChangeMonthlyRecurringSection, donation_type]);

  const netBankingSection = React.useMemo(
    () => ({
      ...offlineSectionDetails.standingInstructionDetails.netBankingDetails,
      onChange: (netBankingDetails) =>
        onChangeStandingInstructionDetails({
          netBankingDetails,
        }),
    }),
    [
      offlineSectionDetails.standingInstructionDetails.netBankingDetails,
      onChangeStandingInstructionDetails,
    ]
  );

  const walletSection = React.useMemo(
    () => ({
      ...offlineSectionDetails.standingInstructionDetails.walletDetails,
      onChange: (walletDetails) =>
        onChangeStandingInstructionDetails({ walletDetails }),
    }),
    [
      offlineSectionDetails.standingInstructionDetails.walletDetails,
      onChangeStandingInstructionDetails,
    ]
  );

  const onlineSection = React.useMemo(
    () => ({
      ...onlineSectionDetails,
      onChange: (onlineSectionDetail) =>
        onChangeMonthlyRecurringSection({
          onlineSectionDetails: onlineSectionDetail,
        }),
    }),
    [onlineSectionDetails, onChangeMonthlyRecurringSection]
  );

  return {
    isMonthlyRecurringDonation,
    monthlyRecurringSwitch,
    donationTypeOptions,
    paymentOptionRadios,
    paymentModeRadios,
    netBankingSection,
    onlineSection,
    walletSection,
    showOnlineSection: React.useMemo(
      () => donation_type === donationTypeOptions[0].value,
      [donation_type, donationTypeOptions]
    ),
    showStandingInstructionSection: React.useMemo(
      () => offlineSectionDetails.payment_option === paymentOptions[0].value,
      [offlineSectionDetails.payment_option, paymentOptions]
    ),
    showECSSection: React.useMemo(
      () => offlineSectionDetails.payment_option === paymentOptions[1].value,
      [offlineSectionDetails.payment_option, paymentOptions]
    ),
    showNetBankingSection: React.useMemo(
      () =>
        offlineSectionDetails.standingInstructionDetails.payment_mode ===
        paymentModes[0].value,
      [
        offlineSectionDetails.standingInstructionDetails.payment_mode,
        paymentModes,
      ]
    ),
    showWalletSection: React.useMemo(
      () =>
        offlineSectionDetails.standingInstructionDetails.payment_mode ===
        paymentModes[1].value,
      [
        offlineSectionDetails.standingInstructionDetails.payment_mode,
        paymentModes,
      ]
    ),
    showOfflineRecurringSection: React.useMemo(
      () => donation_type === donationTypeOptions[1].value,
      [donation_type, donationTypeOptions]
    ),
  };
};

const RecurringDonationSection = ({
  isMonthlyRecurringDonation,
  onlineSection,
  showOnlineSection,
  showOfflineRecurringSection,
  paymentOptionRadios,
  showStandingInstructionSection,
  paymentModeRadios,
  showNetBankingSection,
  netBankingSection,
  showWalletSection,
  walletSection,
  showECSSection,
  org,
  removeAccountType,
}) => (
  <FlexColumn gap="13px" sx={{ color: "text", mt: 2 }}>
    {/* <Box sx={{ fontSize: "14px", mb: "0px !important" }}>
      <Switch
        label="Setup a monthly recurring donation"
        sx={{ color: "text" }}
        {...monthlyRecurringSwitch}
        disabled
      />
    </Box> */}
    {isMonthlyRecurringDonation && (
      <FlexColumn gap="13px" sx={{ color: "text" }}>
        {/* DONATION TYPE RADIOS */}
        {/* Removed optional donations  */}
        {/* <RadiosWithLabel {...donationTypeRadios} label="Donation Type" /> */}

        {/* ONLINE DONATION */}
        {showOnlineSection && (
          <OnlineSection
            {...onlineSection}
            org={org}
            removeAccountType={removeAccountType}
          />
        )}

        {/* OFFLINE DONATION */}
        {showOfflineRecurringSection && (
          <>
            <RadiosWithLabel
              style={{ fontSize: "13px" }}
              label="Payment Option"
              radiosWrapper={Box}
              {...paymentOptionRadios}
            />

            {/* STANDING INSTRUCTION SECTION */}
            {showStandingInstructionSection && (
              <>
                <RadiosWithLabel
                  style={{ fontSize: "13px" }}
                  label="Payment Mode"
                  {...paymentModeRadios}
                />

                {/* NET BANKING SECTION */}
                {showNetBankingSection && (
                  <NetBankingSection {...netBankingSection} />
                )}

                {/* SHOW WALLET SECTION */}
                {showWalletSection && <WalletSection {...walletSection} />}
              </>
            )}

            {/* ECS Section */}
            {showECSSection && <ECSSection />}
          </>
        )}
      </FlexColumn>
    )}
  </FlexColumn>
);

RecurringDonationSection.propTypes = {
  isMonthlyRecurringDonation: PropTypes.bool,
  monthlyRecurringSwitch: PropTypes.shape({}),
  donationTypeRadios: PropTypes.shape({}),
  onlineSection: PropTypes.shape({}),
  paymentOptionRadios: PropTypes.shape({}),
  paymentModeRadios: PropTypes.shape({}),
  netBankingSection: PropTypes.shape({}),
  walletSection: PropTypes.shape({}),
  showOnlineSection: PropTypes.bool,
  showOfflineRecurringSection: PropTypes.bool,
  showStandingInstructionSection: PropTypes.bool,
  showECSSection: PropTypes.bool,
  showNetBankingSection: PropTypes.bool,
  showWalletSection: PropTypes.bool,
  org: PropTypes.string,
  removeAccountType: PropTypes.bool,
};

RecurringDonationSection.defaultProps = {
  isMonthlyRecurringDonation: false,
  monthlyRecurringSwitch: {},
  donationTypeRadios: {},
  onlineSection: {},
  paymentOptionRadios: {},
  paymentModeRadios: {},
  netBankingSection: {},
  walletSection: {},
  showOnlineSection: false,
  showOfflineRecurringSection: false,
  showECSSection: false,
  showNetBankingSection: false,
  showWalletSection: false,
  showStandingInstructionSection: false,
  org: "",
  removeAccountType: false,
};

export default React.memo(RecurringDonationSection);
